import { useEffect, useRef, useState } from 'react';

import { getEvent, getEventOpportunity, type EventDetail, type EventOpportunity } from '../../lib/catalog';
import { CopyProvider } from '../../copy/CopyProvider';
import { CmsContextProvider } from '../../cms/context';
import { CmsThemeProvider } from '../../cms/ThemeProvider';
import { DocumentRenderer, type RenderProblem } from '../../cms/BlockRenderer';
import type { BlockDocument } from '../../cms/editor/document';
import type { BlockDocument as RenderDocument } from '../../cms/BlockRenderer';
import { ApiError } from '../../lib/apiClient';

/**
 * The centre region: the draft, rendered by the same code the public page runs.
 *
 * ── THE SAME RENDERER, NOT A FACSIMILE ───────────────────────────────────────────────
 *
 * This mounts DocumentRenderer — the exact component EventPageHost mounts. An editor
 * preview drawn by a second, editor-only renderer is a preview of something nobody will
 * ever see, and it drifts the first time either side changes. The consequence is that
 * every guarantee the runtime has (unknown components skipped, version mismatches
 * refused, a non-critical block's error boundary) behaves here exactly as it will in
 * production, and the problems it reports are shown rather than swallowed.
 *
 * ── IT RENDERS AGAINST A REAL EVENT ──────────────────────────────────────────────────
 *
 * Blocks read their data from the event context, never from the document. So a preview
 * needs an event, and it uses a real one — placeholder data would hide precisely the
 * problems worth catching, like a lineup block on an event with no lineup.
 */
export function EditorPreview({
  document, previewEventId, context = 'event', width = 'desktop', onProblems,
}: {
  document: BlockDocument;
  previewEventId: string | null;
  /** 'global_section' renders without an event: section blocks never read one. */
  context?: 'event' | 'global_section';
  width?: 'desktop' | 'mobile';
  onProblems?: (problems: RenderProblem[]) => void;
}) {
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [opportunity, setOpportunity] = useState<EventOpportunity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!previewEventId) { setEvent(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEvent(previewEventId)
      .then((detail) => { if (!cancelled) setEvent(detail); })
      .catch((err) => {
        if (cancelled) return;
        setEvent(null);
        setError(err instanceof ApiError && err.status === 404
          ? 'That event is not publicly readable, so it cannot be used for a preview. '
            + 'Choose a published event.'
          : 'The preview event could not be loaded.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    getEventOpportunity(previewEventId)
      .then((o) => { if (!cancelled) setOpportunity(o); })
      .catch(() => { if (!cancelled) setOpportunity(null); });

    return () => { cancelled = true; };
  }, [previewEventId]);

  // ── COLLECTING WHAT THE RENDERER REFUSED ──────────────────────────────────
  //
  // onProblem is called DURING render, so it cannot set state — that would be a state
  // update inside another component's render pass. Problems go into a ref, and an effect
  // that runs after the pass reports them. The signature comparison is what stops the
  // report from re-rendering the preview, which would collect the same problems again.
  const collected = useRef<RenderProblem[]>([]);
  const reported = useRef('');
  collected.current = [];

  useEffect(() => {
    const signature = JSON.stringify(collected.current);
    if (signature === reported.current) return;
    reported.current = signature;
    onProblems?.(collected.current);
  });

  // ── A GLOBAL SECTION HAS NO ENTITY, AND THAT IS THE POINT ─────────────────
  //
  // §46/§61. A footer renders on every page, so it cannot depend on any one of them.
  // Its blocks are contexts:['global_section'] and read nothing from an event, so the
  // preview needs no event — and the theme it shows is the HOST's, which is why the
  // provider here supplies the default bundle rather than one belonging to the section.
  if (context === 'global_section') {
    return (
      <div className={`cms-preview cms-preview--${width}`}>
        <CmsThemeProvider theme={null}>
          <DocumentRenderer
            document={document as RenderDocument}
            context="global_section"
            onProblem={(problem) => collected.current.push(problem)}
          />
        </CmsThemeProvider>
      </div>
    );
  }

  if (!previewEventId) {
    return (
      <div className="cms-preview cms-preview--empty">
        <p className="adm-muted">
          Choose an event above to see this layout with real content. Blocks read the
          event from the page they are on, so there is nothing to show without one.
        </p>
      </div>
    );
  }

  if (loading) return <div className="cms-preview cms-preview--empty"><p className="adm-muted">Loading the preview event…</p></div>;
  if (error) return <div className="cms-preview cms-preview--empty"><p className="alert alert--error" role="alert">{error}</p></div>;
  if (!event) return null;

  return (
    <div className={`cms-preview cms-preview--${width}`}>
      <CopyProvider scope={{ eventId: event.id }}>
        <CmsThemeProvider theme={null}>
          <CmsContextProvider value={{ kind: 'event', event, opportunity }}>
            <DocumentRenderer
              document={document as RenderDocument}
              context="event"
              onProblem={(problem) => collected.current.push(problem)}
            />
          </CmsContextProvider>
        </CmsThemeProvider>
      </CopyProvider>
    </div>
  );
}
